// ── PullFromRemoteSection — RFC 8628 device auth pull flow ─────────────────
//
// Pairs with a remote environment via device-auth, then creates an async
// pull job on the LOCAL BFF.  The BFF worker fetches the remote manifest
// in the background; this component polls until ready, then surfaces a
// "Load into Viewer" action.
//
// Architecture: identical to PushToRemoteSection but reversed.
//   Push: local manifest → remote /queue
//   Pull: remote /export/batch → local job → manifest ready → load
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Download,
  Loader2,
  ExternalLink,
  Check,
  AlertTriangle,
  X,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Camera,
} from 'lucide-react';
import type { EnvironmentDef, PreviewSummary, ScopeManifest } from '../types';
import type { PullJobStatus } from '../../../api/pullPackageApi';
import type { PullJobProgress } from '../hooks/usePullJob';
import { previewExportCounts, type ExportPreviewCounts } from '../../../api/scopeMigrateApi';
import { fmtBytes as formatBytes } from '@/utils/format';

// ── Types ───────────────────────────────────────────────────────────────────

interface PullFromRemoteSectionProps {
  sourceEnv: string;
  getEnvDef: (key: string) => EnvironmentDef | undefined;
  sourceBffUrl: string | null;
  activeTenantId: number | '' | undefined;

  // Service-to-service auth — when true, skip device auth entirely.
  serviceAuthAvailable: boolean;

  // Device auth state
  devicePhase: string;
  userCode: string | null;
  verificationUri: string | null;
  expiresIn: number | null;
  deviceError: string | null;
  hasCachedSession: (url: string, scope: string) => boolean;
  accessToken: string | null;

  // Device auth actions
  onPairRemote: () => void;
  onCancelAuth: () => void;
  onResetAuth: () => void;
  onDisconnect: () => void;

  // Pull job state (from usePullJob)
  activeJobId: string | null;
  job: { status: PullJobStatus; progress: string | Record<string, unknown> | null; error: string | null } | null;
  isPolling: boolean;
  isCreating: boolean;
  pullManifest: ScopeManifest | null;
  pullError: string | null;
  pullProgress: PullJobProgress | null;
  pullElapsedMs: number;
  pullTimedOut: boolean;
  onCreatePull: (params: {
    root_scope_ids: number[];
    source_env: string;
    tenant_id?: number | null;
    include_outcomes: boolean;
    include_hot_stream: boolean;
    include_gcs_files: boolean;
    include_archives: boolean;
    max_depth: number;
    remote_url: string;
    access_token?: string | null;
  }) => void;
  onCancelPull: () => void;
  onClearPull: () => void;

  // Load into viewer callback
  onLoadManifest: (m: ScopeManifest) => void;

  // Scope selection — root scope IDs from the selection panel (may be empty for full pull)
  selectedRootScopeIds: number[];

  // Preview summary from source scope selection (for real-time pull preview counts)
  previewSummary: PreviewSummary | null;

  // Snapshot browsing — browse remote before pulling
  onBrowseRemote?: () => void;
  snapshotActive?: boolean;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<PullJobStatus, string> = {
  pending: 'Queued',
  exporting: 'Exporting…',
  ready: 'Ready',
  failed: 'Failed',
  expired: 'Expired',
};

const STATUS_COLORS: Record<PullJobStatus, string> = {
  pending: 'text-amber-400',
  exporting: 'text-sky-400',
  ready: 'text-emerald-400',
  failed: 'text-rose-400',
  expired: 'text-neutral-500',
};

function formatScopeCount(m: ScopeManifest | null): string {
  if (!m) return '';
  const parts: string[] = [];
  parts.push(`${m.scopes?.length ?? 0} scopes`);
  if (m.outcomes?.length) parts.push(`${m.outcomes.length} outcomes`);
  if (m.hot_stream?.length) parts.push(`${m.hot_stream.length} hot-stream`);
  if (m.gcs_files?.length) parts.push(`${m.gcs_files.length} GCS files`);
  if (m.hot_archive_files?.length) parts.push(`${m.hot_archive_files.length} archive files`);
  return parts.join(' · ');
}

/** Format elapsed milliseconds into m:ss. */
function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${String(sec).padStart(2, '0')}`;
}

/** Format byte count as human-readable. */

// ── Component ───────────────────────────────────────────────────────────────

export function PullFromRemoteSection({
  sourceEnv,
  getEnvDef,
  sourceBffUrl,
  activeTenantId,
  serviceAuthAvailable,
  devicePhase,
  userCode,
  verificationUri,
  expiresIn,
  deviceError,
  hasCachedSession,
  accessToken,
  onPairRemote,
  onCancelAuth,
  onResetAuth,
  onDisconnect,
  activeJobId,
  job,
  isPolling,
  isCreating,
  pullManifest,
  pullError,
  pullProgress,
  pullElapsedMs,
  pullTimedOut,
  onCreatePull,
  onCancelPull,
  onClearPull,
  onLoadManifest,
  selectedRootScopeIds,
  previewSummary,
  onBrowseRemote,
  snapshotActive,
}: PullFromRemoteSectionProps) {
  // ── Data layer toggles ────────────────────────────────────────────
  const [includeOutcomes, setIncludeOutcomes] = useState(true);
  const [includeHotStream, setIncludeHotStream] = useState(false);
  const [includeGcsFiles, setIncludeGcsFiles] = useState(false);
  const [includeArchives, setIncludeArchives] = useState(false);
  const [maxDepth, setMaxDepth] = useState(99);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const sourceName = getEnvDef(sourceEnv)?.label ?? sourceEnv;

  // ── Auto-trigger auth when idle + sourceBffUrl ready ──────────────
  // When service auth is available, skip device auth entirely.
  const autoTriggered = useRef(false);
  useEffect(() => {
    if (serviceAuthAvailable) return; // no device auth needed
    if (
      sourceBffUrl &&
      devicePhase === 'idle' &&
      !pullManifest &&
      !autoTriggered.current
    ) {
      autoTriggered.current = true;
      onPairRemote();
    }
  }, [sourceBffUrl, devicePhase, pullManifest, onPairRemote, serviceAuthAvailable]);

  // Reset auto-trigger flag when source env changes
  useEffect(() => {
    autoTriggered.current = false;
  }, [sourceBffUrl]);

  // ── Server-side preview counts ────────────────────────────────────
  const [serverCounts, setServerCounts] = useState<ExportPreviewCounts | null>(null);
  const [countsLoading, setCountsLoading] = useState(false);
  const countsReqId = useRef(0);

  useEffect(() => {
    if (selectedRootScopeIds.length === 0 || (!serviceAuthAvailable && devicePhase !== 'approved')) {
      setServerCounts(null);
      return;
    }
    const id = ++countsReqId.current;
    setCountsLoading(true);
    setServerCounts(null);
    previewExportCounts({
      root_scope_ids: selectedRootScopeIds,
      tenant_id: activeTenantId !== '' ? (activeTenantId as number) : undefined,
      include_children: true,
      max_depth: maxDepth,
      include_outcomes: includeOutcomes,
      include_hot_stream: includeHotStream,
    })
      .then((c) => { if (id === countsReqId.current) setServerCounts(c); })
      .catch(() => { /* keep heuristic fallback */ })
      .finally(() => { if (id === countsReqId.current) setCountsLoading(false); });
  }, [selectedRootScopeIds, activeTenantId, devicePhase, maxDepth, includeOutcomes, includeHotStream]);

  /** Render a count with loading spinner fallback. */
  const countCell = (value: number | undefined, fallback: number | string | undefined) => {
    if (countsLoading) return <Loader2 className="w-3 h-3 animate-spin text-neutral-500" />;
    return value != null ? value.toLocaleString() : (fallback ?? '—');
  };

  // ── Build params & trigger pull ───────────────────────────────────
  const handleStartPull = useCallback(() => {
    if (!sourceBffUrl) return;
    // Service auth: access_token not needed — BFF uses shared secret.
    // Device auth: access_token required.
    if (!serviceAuthAvailable && !accessToken) return;
    onCreatePull({
      root_scope_ids: selectedRootScopeIds.length > 0 ? selectedRootScopeIds : [],
      source_env: sourceEnv,
      tenant_id: activeTenantId !== '' ? activeTenantId : null,
      include_outcomes: includeOutcomes,
      include_hot_stream: includeHotStream,
      include_gcs_files: includeGcsFiles,
      include_archives: includeArchives,
      max_depth: maxDepth,
      remote_url: sourceBffUrl,
      ...(accessToken ? { access_token: accessToken } : {}),
    });
  }, [
    sourceBffUrl, accessToken, serviceAuthAvailable, selectedRootScopeIds, sourceEnv,
    activeTenantId, includeOutcomes, includeHotStream, includeGcsFiles,
    includeArchives, maxDepth, onCreatePull,
  ]);

  // Whether the user can start a pull right now.
  // Service auth: always ready once sourceBffUrl is set.
  // Device auth: requires approved phase + accessToken.
  const isAuthReady = serviceAuthAvailable || (devicePhase === 'approved' && !!accessToken);
  const canPull = !!(
    sourceBffUrl &&
    isAuthReady &&
    !isCreating &&
    !isPolling &&
    !pullManifest
  );

  // ── Determine current visual phase ────────────────────────────────
  const hasActiveJob = !!(activeJobId && job && job.status !== 'ready' && job.status !== 'failed' && job.status !== 'expired');
  const hasResult = !!pullManifest;
  const hasError = !!pullError && !hasResult;

  return (
    <div className="px-3 py-2.5 border-t border-neutral-800 pac-glass-subtle shrink-0 space-y-2">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs text-neutral-300 font-medium">
          <Download className="w-3.5 h-3.5 text-cyan-400" />
          Pull from Remote
        </div>
        {sourceBffUrl ? (
          <span
            className="text-[9px] text-neutral-600 font-mono truncate max-w-[180px]"
            title={sourceBffUrl}
          >
            {sourceBffUrl}
          </span>
        ) : (
          <span className="text-[9px] text-amber-500">
            No bff_base_url for source env
          </span>
        )}
      </div>

      {/* ── Phase: service auth — zero-interaction pull ready ── */}
      {serviceAuthAvailable && !hasResult && !hasActiveJob && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-[10px] text-emerald-400">
            <Check className="w-3 h-3" />
            Service Auth — {sourceName}
          </div>

          {/* Transfer summary — server-side counts grid */}
          {previewSummary && selectedRootScopeIds.length > 0 && (
            <div className="space-y-1.5">
              <div className="grid grid-cols-4 gap-1.5">
                <div className="pac-glass rounded px-1.5 py-1">
                  <div className="text-[8px] text-neutral-600 uppercase tracking-wider">Roots</div>
                  <div className="text-xs text-neutral-200 font-medium tabular-nums">{previewSummary.rootCount}</div>
                </div>
                <div className="pac-glass rounded px-1.5 py-1">
                  <div className="text-[8px] text-neutral-600 uppercase tracking-wider">Scopes</div>
                  <div className="text-xs text-neutral-200 font-medium tabular-nums">{countCell(serverCounts?.scope_count, previewSummary.totalScopes)}</div>
                </div>
                <div className="pac-glass rounded px-1.5 py-1">
                  <div className="text-[8px] text-neutral-600 uppercase tracking-wider">Depth</div>
                  <div className="text-xs text-neutral-200 font-medium tabular-nums">{previewSummary.treeDepth}</div>
                </div>
                <div className="pac-glass rounded px-1.5 py-1">
                  <div className="text-[8px] text-neutral-600 uppercase tracking-wider">NS</div>
                  <div className="text-xs text-neutral-200 font-medium tabular-nums">{previewSummary.distinctNamespaces.length}</div>
                </div>
              </div>
            </div>
          )}

          {/* Data layer toggles with server counts */}
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[10px]">
            <label className="flex items-center gap-1.5 cursor-pointer text-neutral-300">
              <input type="checkbox" checked={includeOutcomes} onChange={(e) => setIncludeOutcomes(e.target.checked)} className="accent-cyan-500 w-3 h-3" />
              Outcomes
              {includeOutcomes && <span className="text-emerald-400/80 font-medium tabular-nums">{countCell(serverCounts?.outcome_count, previewSummary?.estimatedOutcomeCount)}</span>}
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer text-neutral-300">
              <input type="checkbox" checked={includeHotStream} onChange={(e) => setIncludeHotStream(e.target.checked)} className="accent-cyan-500 w-3 h-3" />
              Hot Stream
              {includeHotStream && <span className="text-amber-400/80 font-medium tabular-nums">{countCell(serverCounts?.hot_stream_count, previewSummary?.estimatedHotStreamCount)}</span>}
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer text-neutral-300">
              <input type="checkbox" checked={includeGcsFiles} onChange={(e) => setIncludeGcsFiles(e.target.checked)} className="accent-cyan-500 w-3 h-3" />
              GCS Artifacts
              {includeGcsFiles && <span className="text-sky-400/80 font-medium tabular-nums">{countCell(serverCounts?.gcs_file_count, previewSummary?.artifactFileCount)}</span>}
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer text-neutral-300">
              <input type="checkbox" checked={includeArchives} onChange={(e) => setIncludeArchives(e.target.checked)} className="accent-cyan-500 w-3 h-3" />
              Archives
              {includeArchives && serverCounts?.archive_file_count != null && serverCounts.archive_file_count > 0 && (
                <span className="text-violet-400/80 font-medium tabular-nums">{serverCounts.archive_file_count.toLocaleString()}</span>
              )}
            </label>
          </div>

          {/* Pull button + Browse Remote */}
          <div className="flex gap-2">
            {onBrowseRemote && (
              <button
                onClick={onBrowseRemote}
                disabled={snapshotActive}
                className={
                  'flex-1 px-3 py-1.5 rounded border text-xs flex items-center justify-center gap-1.5 transition-colors ' +
                  (snapshotActive
                    ? 'border-sky-700 text-sky-300 bg-sky-950/30'
                    : 'border-sky-800 text-sky-300 hover:border-sky-600 hover:bg-sky-950/20')
                }
              >
                <Camera className="w-3 h-3" />
                {snapshotActive ? 'Browsing Remote…' : 'Browse Remote'}
              </button>
            )}
            <button
              disabled={!canPull}
              onClick={handleStartPull}
              className={
                (onBrowseRemote ? 'flex-1 ' : 'w-full ') +
                'px-3 py-1.5 rounded border text-xs flex items-center justify-center gap-1.5 transition-colors ' +
                (canPull
                  ? 'border-cyan-700 text-cyan-200 hover:border-cyan-500 hover:bg-cyan-950/40'
                  : 'border-neutral-700 text-neutral-500 cursor-not-allowed')
              }
            >
              {isCreating ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> Creating job…</>
              ) : (
                <><Download className="w-3 h-3" />
                  {selectedRootScopeIds.length > 0
                    ? `Pull ${selectedRootScopeIds.length} scope(s)`
                    : `Direct Pull`}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── Phase: idle — auto-pairing in progress ── */}
      {!serviceAuthAvailable && devicePhase === 'idle' && !hasResult && !hasActiveJob && (
        <div className="flex items-center gap-2 text-xs text-neutral-400">
          {sourceBffUrl ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-400" />
              Connecting to {sourceName}…
            </>
          ) : (
            <span className="text-amber-500 text-[10px]">No BFF URL for source env</span>
          )}
        </div>
      )}

      {/* ── Phase: requesting ── */}
      {!serviceAuthAvailable && devicePhase === 'requesting' && (
        <div className="flex items-center gap-2 text-xs text-neutral-400">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-400" />
          Connecting…
        </div>
      )}

      {/* ── Phase: awaiting_approval / polling device code ── */}
      {!serviceAuthAvailable && (devicePhase === 'awaiting_approval' || devicePhase === 'polling') && (
        <div className="border border-cyan-800/60 bg-cyan-950/30 rounded-lg p-3 space-y-2.5">
          <div className="text-[10px] text-neutral-400">
            Open the remote admin panel and enter this code in{' '}
            <span className="text-cyan-300 font-medium">Device Approvals</span>:
          </div>
          <div className="flex items-center justify-center gap-3 py-2">
            <span className="text-2xl font-mono font-bold tracking-[0.25em] text-cyan-200 select-all">
              {userCode}
            </span>
          </div>
          <div className="flex items-center justify-between text-[9px]">
            <div className="flex items-center gap-1.5">
              {devicePhase === 'polling' ? (
                <Loader2 className="w-3 h-3 animate-spin text-cyan-400" />
              ) : (
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              )}
              <span className="text-neutral-500">
                {devicePhase === 'polling' ? 'Polling for approval…' : 'Waiting for admin approval…'}
              </span>
            </div>
            {expiresIn != null && (
              <span className="text-neutral-600 tabular-nums">
                TTL {Math.floor(expiresIn / 60)}:{String(expiresIn % 60).padStart(2, '0')}
              </span>
            )}
          </div>
          {(verificationUri || (userCode && sourceBffUrl)) && (
            <a
              href={sourceBffUrl && userCode
                ? `/device?user_code=${encodeURIComponent(userCode)}&target=${encodeURIComponent(sourceBffUrl)}`
                : (verificationUri ?? '#')}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[10px] text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              Open verification page
            </a>
          )}
          <button
            onClick={onCancelAuth}
            className="px-2 py-1 rounded border border-neutral-700 text-[10px] text-neutral-400 hover:border-neutral-500 hover:text-neutral-300"
          >
            Cancel
          </button>
        </div>
      )}

      {/* ── Phase: approved — data layer toggles + pull button ── */}
      {!serviceAuthAvailable && devicePhase === 'approved' && !hasResult && !hasActiveJob && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-[10px] text-emerald-400">
            <Check className="w-3 h-3" />
            Paired with {sourceName}
          </div>

          {/* Transfer summary — server-side counts grid */}
          {previewSummary && selectedRootScopeIds.length > 0 && (
            <div className="space-y-1.5">
              <div className="grid grid-cols-4 gap-1.5">
                <div className="pac-glass rounded px-1.5 py-1">
                  <div className="text-[8px] text-neutral-600 uppercase tracking-wider">Roots</div>
                  <div className="text-xs text-neutral-200 font-medium tabular-nums">{previewSummary.rootCount}</div>
                </div>
                <div className="pac-glass rounded px-1.5 py-1">
                  <div className="text-[8px] text-neutral-600 uppercase tracking-wider">Scopes</div>
                  <div className="text-xs text-neutral-200 font-medium tabular-nums">{countCell(serverCounts?.scope_count, previewSummary.totalScopes)}</div>
                </div>
                <div className="pac-glass rounded px-1.5 py-1">
                  <div className="text-[8px] text-neutral-600 uppercase tracking-wider">Depth</div>
                  <div className="text-xs text-neutral-200 font-medium tabular-nums">{previewSummary.treeDepth}</div>
                </div>
                <div className="pac-glass rounded px-1.5 py-1">
                  <div className="text-[8px] text-neutral-600 uppercase tracking-wider">NS</div>
                  <div className="text-xs text-neutral-200 font-medium tabular-nums">{previewSummary.distinctNamespaces.length}</div>
                </div>
              </div>
            </div>
          )}

          {/* Data layer toggles with server counts */}
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[10px]">
            <label className="flex items-center gap-1.5 cursor-pointer text-neutral-300">
              <input
                type="checkbox"
                checked={includeOutcomes}
                onChange={(e) => setIncludeOutcomes(e.target.checked)}
                className="accent-cyan-500 w-3 h-3"
              />
              Outcomes
              {includeOutcomes && (
                <span className="text-emerald-400/80 font-medium tabular-nums">
                  {countCell(serverCounts?.outcome_count, previewSummary?.estimatedOutcomeCount)}
                </span>
              )}
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer text-neutral-300">
              <input
                type="checkbox"
                checked={includeHotStream}
                onChange={(e) => setIncludeHotStream(e.target.checked)}
                className="accent-cyan-500 w-3 h-3"
              />
              Hot Stream
              {includeHotStream && (
                <span className="text-amber-400/80 font-medium tabular-nums">
                  {countCell(serverCounts?.hot_stream_count, previewSummary?.estimatedHotStreamCount)}
                </span>
              )}
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer text-neutral-300">
              <input
                type="checkbox"
                checked={includeGcsFiles}
                onChange={(e) => setIncludeGcsFiles(e.target.checked)}
                className="accent-cyan-500 w-3 h-3"
              />
              GCS Artifacts
              {includeGcsFiles && (
                <span className="text-sky-400/80 font-medium tabular-nums">
                  {countCell(serverCounts?.gcs_file_count, previewSummary?.artifactFileCount)}
                </span>
              )}
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer text-neutral-300">
              <input
                type="checkbox"
                checked={includeArchives}
                onChange={(e) => setIncludeArchives(e.target.checked)}
                className="accent-cyan-500 w-3 h-3"
              />
              Archives
              {includeArchives && serverCounts?.archive_file_count != null && serverCounts.archive_file_count > 0 && (
                <span className="text-violet-400/80 font-medium tabular-nums">
                  {serverCounts.archive_file_count.toLocaleString()}
                </span>
              )}
            </label>
          </div>

          {/* Advanced toggle */}
          <button
            onClick={() => setShowAdvanced((v) => !v)}
            className="flex items-center gap-1 text-[9px] text-neutral-600 hover:text-neutral-400 transition-colors"
          >
            {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            Advanced
          </button>
          {showAdvanced && (
            <div className="flex items-center gap-2 text-[10px] text-neutral-400 pl-1">
              <label className="flex items-center gap-1">
                Max depth:
                <input
                  type="number"
                  min={1}
                  max={99}
                  value={maxDepth}
                  onChange={(e) => setMaxDepth(Number(e.target.value) || 99)}
                  className="w-12 px-1 py-0.5 rounded pac-glass-input text-neutral-300 text-[10px] font-mono outline-none focus:border-cyan-500"
                />
              </label>
            </div>
          )}

          {/* Pull button + Browse Remote */}
          <div className="flex gap-2">
            {onBrowseRemote && (
              <button
                onClick={onBrowseRemote}
                disabled={snapshotActive}
                className={
                  'flex-1 px-3 py-1.5 rounded border text-xs flex items-center justify-center gap-1.5 transition-colors ' +
                  (snapshotActive
                    ? 'border-sky-700 text-sky-300 bg-sky-950/30'
                    : 'border-sky-800 text-sky-300 hover:border-sky-600 hover:bg-sky-950/20')
                }
              >
                <Camera className="w-3 h-3" />
                {snapshotActive ? 'Browsing…' : 'Browse Remote'}
              </button>
            )}
            <button
              disabled={!canPull}
              onClick={handleStartPull}
              className={
                (onBrowseRemote ? 'flex-1 ' : 'w-full ') +
                'px-3 py-1.5 rounded border text-xs flex items-center justify-center gap-1.5 transition-colors ' +
                (canPull
                  ? 'border-cyan-700 text-cyan-200 hover:border-cyan-500 hover:bg-cyan-950/40'
                  : 'border-neutral-700 text-neutral-500 cursor-not-allowed')
              }
            >
              {isCreating ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> Creating job…</>
              ) : (
                <><Download className="w-3 h-3" />
                  {selectedRootScopeIds.length > 0
                    ? `Pull ${selectedRootScopeIds.length} scope(s)`
                    : `Direct Pull`}
                </>
              )}
            </button>
          </div>

          <button
            onClick={onDisconnect}
            className="text-[9px] text-neutral-600 hover:text-neutral-400 transition-colors"
          >
            Disconnect
          </button>
        </div>
      )}

      {/* ── Active job — rich progress indicator ── */}
      {hasActiveJob && (
        <div className={
          'border rounded-lg p-2.5 space-y-2 ' +
          (pullTimedOut
            ? 'border-amber-800/60 bg-amber-950/20'
            : 'border-cyan-800/50 bg-cyan-950/20')
        }>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-[10px]">
              {pullTimedOut ? (
                <AlertTriangle className="w-3 h-3 text-amber-500" />
              ) : (
                <Loader2 className="w-3 h-3 animate-spin text-cyan-400" />
              )}
              <span className={pullTimedOut ? 'text-amber-400' : STATUS_COLORS[job!.status]}>
                {pullTimedOut ? 'Timed Out' : STATUS_LABELS[job!.status]}
              </span>
            </div>
            <div className="flex items-center gap-2 text-[9px] text-neutral-600 font-mono">
              <span className="tabular-nums">{formatElapsed(pullElapsedMs)}</span>
              <span>{activeJobId?.slice(0, 8)}</span>
            </div>
          </div>

          {/* Progress details from BFF worker */}
          {pullProgress && (
            <div className="space-y-1.5">
              {/* Phase label */}
              {pullProgress.phase && (
                <div className="flex items-center gap-1.5 text-[9px]">
                  <span className="text-neutral-500">Phase:</span>
                  <span className="text-cyan-300 font-medium capitalize">{pullProgress.phase}</span>
                  {pullProgress.auth_method && (
                    <span className="text-neutral-600">via {pullProgress.auth_method}</span>
                  )}
                </div>
              )}

              {/* Frames progress bar */}
              {pullProgress.total_frames != null && pullProgress.total_frames > 0 && (
                <div className="space-y-0.5">
                  <div className="flex items-center justify-between text-[9px]">
                    <span className="text-neutral-500">
                      Frames: {pullProgress.frames_decoded ?? 0} / {pullProgress.total_frames}
                    </span>
                    <span className="text-neutral-600 tabular-nums">
                      {Math.round(((pullProgress.frames_decoded ?? 0) / pullProgress.total_frames) * 100)}%
                    </span>
                  </div>
                  <div className="h-1 rounded-full bg-neutral-800 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-cyan-500 transition-all duration-300"
                      style={{
                        width: `${Math.min(100, ((pullProgress.frames_decoded ?? 0) / pullProgress.total_frames) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Stats row: scopes, outcomes, bytes */}
              <div className="flex items-center gap-3 text-[9px] text-neutral-500">
                {pullProgress.scopes != null && (
                  <span>{pullProgress.scopes} scopes</span>
                )}
                {pullProgress.outcomes != null && (
                  <span>{pullProgress.outcomes} outcomes</span>
                )}
                {pullProgress.bytes_received != null && pullProgress.bytes_received > 0 && (
                  <span>{formatBytes(pullProgress.bytes_received)}</span>
                )}
              </div>
            </div>
          )}

          {/* Fallback: show raw progress string if no structured progress */}
          {!pullProgress && job!.progress && (
            <div className="text-[9px] text-neutral-500">
              {typeof job!.progress === 'string'
                ? job!.progress
                : typeof job!.progress === 'object'
                  ? (job!.progress as any).phase ?? JSON.stringify(job!.progress)
                  : String(job!.progress)}
            </div>
          )}

          {/* Timeout warning */}
          {pullTimedOut && (
            <div className="text-[9px] text-amber-400 leading-relaxed">
              The remote server may be unreachable or the export is too large.
              Cancel and retry, or check the server logs.
            </div>
          )}

          <button
            onClick={onCancelPull}
            className="px-2 py-1 rounded border border-neutral-700 text-[10px] text-neutral-400 hover:border-neutral-500"
          >
            Cancel
          </button>
        </div>
      )}

      {/* ── Pull result — manifest ready ── */}
      {hasResult && pullManifest && (
        <div className="border border-emerald-800/50 bg-emerald-950/30 rounded-lg p-2.5 space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-300">
            <Check className="w-3.5 h-3.5" />
            Manifest ready
          </div>
          <div className="text-[10px] text-neutral-400 leading-relaxed">
            {formatScopeCount(pullManifest)}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onLoadManifest(pullManifest)}
              className="flex-1 px-3 py-1.5 rounded border border-emerald-700 text-emerald-200 text-xs hover:border-emerald-500 hover:bg-emerald-950/40 flex items-center justify-center gap-1.5 transition-colors"
            >
              <Download className="w-3 h-3" />
              Load into Viewer
            </button>
            <button
              onClick={() => {
                onClearPull();
              }}
              className="px-2 py-1 rounded border border-neutral-700 text-[10px] text-neutral-400 hover:border-neutral-500"
            >
              Done
            </button>
          </div>
          <button
            onClick={() => {
              onClearPull();
            }}
            className="flex items-center gap-1 text-[9px] text-neutral-600 hover:text-neutral-400 transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            Pull again
          </button>
        </div>
      )}

      {/* ── Phase: error / expired ── */}
      {!serviceAuthAvailable && (devicePhase === 'error' || devicePhase === 'expired') && !hasResult && (
        <div className="flex items-start gap-1.5 border border-rose-900/50 bg-rose-950/20 rounded p-2">
          <AlertTriangle className="w-3.5 h-3.5 text-rose-500 shrink-0 mt-0.5" />
          <div className="text-[10px] text-rose-400 leading-relaxed">
            {deviceError ?? 'Device auth failed'}
          </div>
          <button
            onClick={onResetAuth}
            className="ml-auto shrink-0 text-[10px] text-neutral-500 hover:text-neutral-300"
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Pull error (non-auth) ── */}
      {hasError && (
        <div className="flex items-start gap-1.5 border border-rose-900/50 bg-rose-950/20 rounded p-2">
          <AlertTriangle className="w-3.5 h-3.5 text-rose-500 shrink-0 mt-0.5" />
          <div className="text-[10px] text-rose-400 leading-relaxed">
            {pullError}
          </div>
          <button
            onClick={onClearPull}
            className="ml-auto shrink-0"
          >
            <X className="w-3 h-3 text-neutral-600 hover:text-neutral-400" />
          </button>
        </div>
      )}
    </div>
  );
}
